/* eslint-disable no-unused-vars */
import create from 'zustand';
import { cfGetAttendeesFromGoogleAttendees } from '../database/Meetings/firebaseMeetingAPI';
import GAPI from '../external/GoogleAPI/GAPI';
import ConsoleImproved from '../shared/classes/ConsoleImproved';
import GapiMeetings from '../shared/classes/GapiMeetings';
import PublicUsers from '../shared/classes/PublicUsers';
import {
  GapiMeetingData,
  GoogleAttendee, PublicUserDataV2, ResolvedState,
} from '../shared/types/types';
import { PENDING, RESOLVED } from '../utils/enums';
import { cfSearchPublicUserDataV2ByEmailsV2 } from '../external/publicUserData/PublicUserDataAPI';

interface AllMeetingsStore {
  resolveState: ResolvedState;
  gapiMeetings: GapiMeetings;
  meetings: GapiMeetingData[],
  fetchMeetingsDaysAgo: number;
  users: PublicUsers;
  // eslint-disable-next-line no-unused-vars
  fetchMeetings: (daysAgo: number, userId: string, alwaysFetch?: boolean) => void;
  // eslint-disable-next-line no-unused-vars
  fetchMeetings7DaysFromStartDay: (daysAgo: number, userId: string) => void;
  // eslint-disable-next-line no-unused-vars
  fetchMeetingsAgain: (daysAgo: number, userId: string) => void;
  fetchUsers: (googleAttendees: GoogleAttendee[]) => Promise<PublicUserDataV2[]>;
  fetchUsersByEmails: (emails: string[]) => Promise<PublicUserDataV2[]>;
}

const useMeetingsAndUsersStore = create<AllMeetingsStore>((set, get) => ({
  resolveState: PENDING,
  gapiMeetings: new GapiMeetings(),
  meetings: [],
  users: new PublicUsers(),
  fetchMeetingsDaysAgo: 0,
  fetchMeetings: async (daysAgo, userId, alwaysFetch = false) => {
    const { resolveState, fetchMeetingsDaysAgo } = get();
    if (resolveState === RESOLVED && fetchMeetingsDaysAgo === daysAgo && !alwaysFetch) {
      ConsoleImproved.log('Meetings already fetched, so not fetching');
      return;
    }

    // const gapiMeetings = await GoogleAPI.getMeetingsLastSevenDays(daysAgo);
    const gapiMeetings = await GapiMeetings.getMeetingsPreviousWeekAndNextWeek();
    set(() => ({
      gapiMeetings,
      meetings: gapiMeetings.meetingsWithNotesOrIsToday,
      fetchMeetingsDaysAgo: daysAgo,
    }));

    await gapiMeetings.checkWhichMeetingsHaveNotes(userId);

    // // const meetingsWithNotes2 = gapiMeetings.getMeetingsLastXDays( userId, daysAgo);
    // // const meetingsWithNotes = await filterMeetingsByThoseThatHaveNotesExceptForToday(
    // //   userId, gapiMeetings.item,
    // // );
    // if (meetingsWithNotes.resolvedState !== RESOLVED) {
    //   ConsoleImproved.log('Failed to fetch meetings',
    //  { gapiMeetings, filteredMeetings: meetingsWithNotes });
    //   set(() => ({
    //     resolveState: meetingsWithNotes.resolvedState,
    //   }));
    //   return;
    // }

    // const googleAttendees = GoogleAPI.mapMeetingsToAttendees(meetingsWithNotes.item);
    await get().fetchUsers(gapiMeetings.googleAttendees);
    set(() => {
      console.log('All Notes fetching data ended at', new Date());
      return {
        gapiMeetings,
        meetings: gapiMeetings.meetingsWithNotesOrIsToday,
        resolveState: RESOLVED,
        fetchMeetingsDaysAgo: daysAgo,
      };
    });
  },
  fetchMeetings7DaysFromStartDay: async (startDay, userId) => {
    set(() => ({
      resolveState: PENDING,
    }));
    const { gapiMeetings } = get();
    await gapiMeetings.getMeetings7DaysFromStartDay(startDay);
    // set(() => ({
    //   gapiMeetings,
    //   resolveState: RESOLVED,
    //   fetchMeetingsDaysAgo: daysAgo,
    // }));
    await gapiMeetings.checkWhichMeetingsHaveNotes(userId);
    set(() => ({
      gapiMeetings,
      meetings: gapiMeetings.meetingsWithNotesOrIsToday,
      resolveState: RESOLVED,
      fetchMeetingsDaysAgo: startDay,
    }));
  },
  fetchMeetingsAgain: async (startDay, userId) => {
    console.log('fetchMeetingsAgain');
    set(() => ({
      resolveState: PENDING,
    }));
    const gapiMeetings = await GapiMeetings.getMeetingsPreviousWeekAndNextWeek();
    let i = startDay;
    while (i < -6) {
      // eslint-disable-next-line no-await-in-loop
      await gapiMeetings.getMeetings7DaysFromStartDay(i);
      i += 7;
    }
    await gapiMeetings.checkWhichMeetingsHaveNotes(userId);
    set(() => ({
      gapiMeetings,
      meetings: gapiMeetings.meetingsWithNotesOrIsToday,
      resolveState: RESOLVED,
      fetchMeetingsDaysAgo: startDay,
    }));
  },
  fetchUsers: async (googleAttendees: GoogleAttendee[]) => {
    const { users } = get();
    const usersNotAlreadyInList = users
      .getGoogleAttendeesNotAlreadyInList(googleAttendees);
    if (usersNotAlreadyInList.length === 0) {
      ConsoleImproved.log('Users already fetched');
      return users.getUsersAlsoInGoogleAttendees(googleAttendees);
    }

    const newUsers = await cfGetAttendeesFromGoogleAttendees(usersNotAlreadyInList);
    users.add(newUsers);
    set(() => ({
      users,
    }));
    return users.getUsersAlsoInGoogleAttendees(googleAttendees);
  },
  fetchUsersByEmails: async (emails: string[]): Promise<PublicUserDataV2[]> => {
    const { users } = get();
    const usersNotAlreadyInList = users.getUsersNotAlreadyInList(emails);
    if (usersNotAlreadyInList.length === 0) {
      ConsoleImproved.log('Users already fetched');
      return users.getUsersByEmails(emails);
    }

    const newUsers = await cfSearchPublicUserDataV2ByEmailsV2(usersNotAlreadyInList);
    users.add(newUsers);
    set(() => ({
      users,
    }));
    return users.getUsersByEmails(emails);
  },
}));

export default useMeetingsAndUsersStore;
